<app-header></app-header>
 <main class="container mx-auto px-4 pb-6 lg:px-6 lg:py-8">
        <!-- Hero Section -->
        <section class="mt-4 mb-8 lg:mb-12">
            <div class="bg-gradient-to-r from-wattpad-primary to-wattpad-secondary rounded-xl p-6 lg:p-8 text-white">
                <div class="flex flex-col lg:flex-row gap-6 items-center">
                    <div class="lg:w-1/3 flex justify-center">
                        <div class="bg-white/20 p-6 rounded-full w-48 h-48 flex items-center justify-center">
                            <i class="fas fa-file-contract text-6xl"></i>
                        </div>
                    </div>
                    <div class="lg:w-2/3 text-center lg:text-left">
                        <h1 class="text-2xl lg:text-4xl font-bold mb-2">📄 Terms of Service – Funtel Stories</h1>
                        <p class="text-lg lg:text-2xl mb-4">Last Updated: 13th March, 2025</p>
                        
                        <p class="text-sm lg:text-base mb-6">
                            Welcome to Funtel Stories! We sincerely thank you for being part of our growing community of writers and readers. 
                            Funtel Stories is designed to provide a creative space where authors can publish their original works and readers can enjoy, 
                            interact with, and support those creations.
                        </p>
                        
                        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <a href="#important-notice" class="bg-white text-wattpad-primary px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors text-lg">
                                Important Notice
                            </a>
                            <a href="#contact" class="bg-white/20 text-white px-8 py-3 rounded-full font-medium hover:bg-white/30 transition-colors text-lg">
                                Contact Us
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Important Notice -->
        <section class="mb-8 lg:mb-12" id="important-notice">
            <div class="bg-wattpad-accent/10 border border-wattpad-accent/20 rounded-xl p-6">
                <div class="flex items-start">
                    <i class="fas fa-exclamation-triangle text-wattpad-accent text-xl mt-1 mr-4"></i>
                    <div>
                        <h3 class="font-bold text-lg mb-2">👉 Important Notice</h3>
                        <p class="text-wattpad-dark dark:text-gray-300 mb-2">
                            These Terms form a legally binding agreement between you and Funtel Studio.
                        </p>
                        <p class="text-wattpad-dark dark:text-gray-300 mb-2">
                            You must be at least 18 years of age to use the Services. If you are under 18, you may use the Services only with the consent and supervision of a parent or legal guardian, who will be responsible for your compliance with these Terms.
                        </p>
                        <p class="text-wattpad-dark dark:text-gray-300">
                            This document is an electronic record under the Information Technology Act, 2000, and the rules made thereunder, and does not require any physical or digital signatures to be binding. These Terms have been formulated in accordance with the Information Technology (Intermediaries Guidelines and Digital Media Ethics Code) Rules, 2021.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Terms of Service Content -->
        <section class="mb-8 lg:mb-12">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                <!-- Definitions -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">2. Definitions</h2>
                    <p class="mb-4">
                        For the purposes of these Terms of Service, the following words and expressions shall have the meanings set out below.
                    </p>
                    
                    <div class="overflow-x-auto">
                        <table class="definition-table">
                            <thead>
                                <tr>
                                    <th>Term</th>
                                    <th>Definition</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Firm / Company / We / Our / Us</strong></td>
                                    <td>Refers to Funtel Stories, a registered partnership firm, which owns and operates the Funtel Stories Website and Application.</td>
                                </tr>
                                <tr>
                                    <td><strong>Website</strong></td>
                                    <td>Refers to stories.funtelstudio.com, the official online platform of Funtel Stories.</td>
                                </tr>
                                <tr>
                                    <td><strong>Application</strong></td>
                                    <td>Refers to the official Funtel Stories mobile app, available for download on Android and iOS.</td>
                                </tr>
                                <tr>
                                    <td><strong>User / You / Your</strong></td>
                                    <td>Refers to any person accessing or using the Website, Application, or Services, whether as an Author or a Reader.</td>
                                </tr>
                                <tr>
                                    <td><strong>Author</strong></td>
                                    <td>A User who uploads, publishes, or shares their own original literary works (stories, episodes, poems, articles, cover images, or any other creative material) on the Website/Application.</td>
                                </tr>
                                <tr>
                                    <td><strong>Reader</strong></td>
                                    <td>A User who accesses, consumes, or interacts with literary works published on the Website/Application by Authors or the Firm.</td>
                                </tr>
                                <tr>
                                    <td><strong>Published Work(s)</strong></td>
                                    <td>Refers to any creative material uploaded and published by an Author on the Website/Application, including text, images, audio, or any other format supported by the Services.</td>
                                </tr>
                                <tr>
                                    <td><strong>Firm Content</strong></td>
                                    <td>Refers to any literary or creative content published directly by Funtel Stories on the Website/Application.</td>
                                </tr>
                                <tr>
                                    <td><strong>Content</strong></td>
                                    <td>Collectively refers to Published Works uploaded by Authors and Firm Content published by Funtel Stories.</td>
                                </tr>
                                <tr>
                                    <td><strong>Features</strong></td>
                                    <td>Refers to additional interactive options provided by the Firm to Users for enriching their experience, including but not limited to:
                                        <ul class="list-disc pl-6 mt-2">
                                            <li>Submitting reviews or ratings</li>
                                            <li>Chat or message features to communicate with other Users</li>
                                            <li>Setting a username, profile photo, bio, or other profile details</li>
                                            <li>Following favorite Authors or saving Content for later reading</li>
                                        </ul>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Inputs</strong></td>
                                    <td>Refers to all material, details, or information uploaded by Users while using the Features, such as reviews, messages, or profile details.</td>
                                </tr>
                                <tr>
                                    <td><strong>Services</strong></td>
                                    <td>Refers to all functionalities provided through the Website/Application, including publishing, reading, reviewing, interacting with Content, using Features, and accessing monetization options.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Eligibility -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">3. Eligibility</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">Minimum Age Requirement</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You must be at least 18 years of age to access and use the Website/Application and Services independently.</li>
                        <li>If you are under 18 years of age, you may only use the Services with the consent and supervision of a parent or legal guardian, who will be responsible for your compliance with these Terms.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">Parental/Guardian Consent</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>If required by Funtel Stories, the parent(s) or legal guardian(s) of a minor user must furnish written confirmation of consent.</li>
                        <li>Without such consent, the minor user must immediately stop using/accessing the Website/Application.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">Capacity to Contract</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>By using the Services, you represent and warrant that you have the full legal capacity to enter into a binding contract under applicable Indian laws.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">Jurisdiction & Compliance</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>These Terms are governed by the laws of India.</li>
                        <li>You agree to use the Services only in compliance with these Terms, applicable laws, rules, and regulations.</li>
                    </ul>
                </div>

                <!-- User Obligations -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">4. User Obligations</h2>
                    <p class="mb-4">
                        By accessing and using the Website/Application and Services of Funtel Stories, you agree to the following obligations:
                    </p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.1 Accuracy of Information</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You must provide complete and accurate information during registration.</li>
                        <li>If there is any change in your details, you must update them or notify Funtel Stories in writing (e-mail permitted).</li>
                        <li>You must not impersonate another individual (living or deceased).</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.2 Confidentiality</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You are solely responsible for maintaining the confidentiality of your account credentials (username, password, etc.).</li>
                        <li>Any activity carried out through your account will be deemed to have been done by you.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.3 Ownership of Content</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You must ensure that any Published Works uploaded are your own original creations, or you have a valid license/assignment to use them.</li>
                        <li>Your Published Works must not infringe upon any third party's rights, including copyright, trademark, privacy, publicity, or other proprietary rights.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.4 Compliance with Guidelines</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You must follow the Community Guidelines, Content Guidelines, and all policies issued by Funtel Stories from time to time.</li>
                        <li>Violations may result in suspension, termination, or other legal action.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.5 Restrictions on Reproduction</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You may not reproduce, copy, or distribute any Content from Funtel Stories on any other medium/platform without prior written consent of Funtel Stories.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.6 Permissions Granted to Funtel Stories</h3>
                    <p class="mb-2">By publishing your works, you grant Funtel Stories a non-exclusive, worldwide, royalty-free license (until your account exists or until your works are removed) to:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Display your name/username as the Author of your Published Works.</li>
                        <li>Adapt, publish, reproduce, process, modify, publicly perform, and display your Published Works for the purpose of:
                            <ul class="list-circle pl-6 mt-2 space-y-1">
                                <li>Distributing them across the Website/Application,</li>
                                <li>Marketing and promoting Funtel Stories and/or your Published Works,</li>
                                <li>Creating snippets, highlights, or combining your works with others (such as "Top Author Lists" or "Featured Stories").</li>
                            </ul>
                        </li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.7 Prohibited Activities</h3>
                    <p class="mb-2">You must not use the Services for:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Any illegal activities, or to encourage violation of law/third-party rights.</li>
                        <li>Uploading materials that contain viruses, malware, or harmful code designed to disrupt systems or resources.</li>
                        <li>Advertising or soliciting products/services through the platform (including promotions embedded in stories, comments, or messages).</li>
                        <li>Approaching or targeting Users for spamming, poaching, or moving them to other content platforms.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.8 Security Obligations</h3>
                    <p class="mb-2">You agree not to:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Probe, scan, or test the vulnerability of the Website/Application.</li>
                        <li>Breach or attempt to circumvent authentication or security measures.</li>
                        <li>Use bots, scripts, cheats, hacks, or unauthorized software to manipulate Services.</li>
                        <li>Overburden the infrastructure of Funtel Stories with unreasonable load.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.9 Fair Conduct</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You must not use fraudulent means to gain coins, earnings, or benefits under monetization programs.</li>
                        <li>Any such wrongful activity may lead to suspension, forfeiture of earnings, and legal action.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.10 Personal Use Only</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Services are provided for personal, non-commercial use unless otherwise permitted.</li>
                        <li>You may not exploit, trace, or collect information about other Users without authorization.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.11 Trademark & Branding</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You must not misuse or imitate the trademark, logo, design, or brand identity of Funtel Stories for unauthorized purposes.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.12 Non-Disparagement</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You must not engage in any action that may harm the reputation of Funtel Stories, its partners, or its community.</li>
                    </ul>
                </div>

                <!-- Rights of the Firm -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">5. Rights of the Firm</h2>
                    <p class="mb-4">
                        By using Funtel Stories, you acknowledge and agree that the Firm reserves the following rights:
                    </p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.1 Removal of Content</h3>
                    <p class="mb-2">Funtel Stories may remove, block, or restrict access to any Published Work(s), Inputs, or Content that it considers:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Objectionable, offensive, or inappropriate</li>
                        <li>In violation of these Terms, Community Guidelines, or applicable law</li>
                        <li>Reported by Users or third parties as infringing their rights</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.2 Suspension or Termination of Accounts</h3>
                    <p class="mb-2">Funtel Stories reserves the right to restrict, suspend, or permanently terminate any User's account if:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>The User violates these Terms of Service or any other policies</li>
                        <li>The User engages in illegal, fraudulent, or harmful conduct</li>
                        <li>Required by law, government order, or in response to complaints</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.3 Intellectual Property</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>The Website, Application, and Services, including all designs, layouts, logos, graphics, interfaces, trademarks, and brand features, are the sole property of Funtel Stories.</li>
                        <li>Users are granted no rights over these except for limited use of the Services in accordance with these Terms.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.4 Firm Content</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>All Firm Content published by Funtel Stories remains the exclusive property of the Firm or its licensors.</li>
                        <li>No part of Firm Content may be copied, redistributed, or exploited without express permission.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.5 Personal Data</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Funtel Stories will collect, store, and process personal data of Users in accordance with the Privacy Policy.</li>
                        <li>By using the Services, you consent to such collection and processing.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.6 Payments & Virtual Currency</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Funtel Stories reserves the right to introduce, modify, or discontinue features related to virtual coins, subscriptions, or earnings.</li>
                        <li>All terms related to monetization will be separately communicated and updated from time to time.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.7 Legal Disclosure</h3>
                    <p class="mb-2">The Firm may disclose information related to Users, Published Works, or Inputs when required by:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Applicable laws,</li>
                        <li>Government or regulatory authorities,</li>
                        <li>Legal investigations into cybersecurity, fraud, or other violations.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.8 Security Enhancements</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Funtel Stories may implement technical and security measures (including AI-driven moderation) to detect plagiarism, copyright violations, or breaches of these Terms.</li>
                    </ul>
                </div>

                <!-- Content Guidelines -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">6. Content Guidelines</h2>
                    <p class="mb-4">
                        All Published Works and Inputs uploaded on Funtel Stories must follow the rules below. These guidelines ensure that the platform remains a safe, respectful, and enjoyable space for all Users.
                    </p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.1 Prohibited Content</h3>
                    <p class="mb-2">You must not upload, publish, or share any Content that is:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Objectionable or unlawful, including material that is grossly harmful, harassing, defamatory, obscene, pornographic, paedophilic, invasive of another's privacy, hateful, blasphemous, or otherwise unlawful in any manner.</li>
                        <li>Violating the rights of others, including copyright, trademark, publicity rights, privacy rights, or any other intellectual property rights.</li>
                        <li>Sexually explicit or exploitative, including pornography or content that promotes sexual abuse or exploitation.</li>
                        <li>Promoting illegal activities, including but not limited to money laundering, gambling, drugs, terrorism, or other criminal conduct.</li>
                        <li>Containing viruses or harmful code designed to disrupt, destroy, or limit functionality of systems.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.2 National Interest & Public Order</h3>
                    <p class="mb-2">Content must not:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Threaten the sovereignty, security, integrity, or defense of India.</li>
                        <li>Affect friendly relations with foreign states.</li>
                        <li>Disturb public order or incite the commission of any cognizable offence.</li>
                        <li>Insult any nation, community, or protected group.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.3 Protection of Minors</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Content that exploits, harms, or endangers minors in any way is strictly prohibited.</li>
                        <li>This includes sexually suggestive depictions, abusive content, or material encouraging unsafe behavior.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.4 Misleading or Offensive Content</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Do not upload Content that deceives or misleads readers about its origin.</li>
                        <li>Avoid material that is grossly offensive, abusive, or threatening in nature.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.5 Reader Reviews & Inputs</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Reviews and comments must be constructive and respectful.</li>
                        <li>Hate speech, personal attacks, spam, or promotional content in reviews will not be tolerated.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.6 Responsibility for Content</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Authors are solely responsible for ensuring that their Published Works comply with these guidelines.</li>
                        <li>Readers are responsible for ensuring that their comments, reviews, and messages follow these standards.</li>
                    </ul>
                </div>

                <!-- Monetization -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">7. Monetization</h2>
                    <p class="mb-4">
                        Funtel Stories provides Authors with opportunities to earn money from their Published Works through various Monetization Plans. By participating, you agree to the following terms:
                    </p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.1 Monetization Plans</h3>
                    <p class="mb-2">The Firm may introduce different monetization models, including but not limited to:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li><strong>Subscription Plans</strong> – Readers may pay a recurring fee to access premium stories or sections.</li>
                        <li><strong>Pay-Per-Episode / Unlocks</strong> – Certain chapters or episodes may be locked and accessible only upon payment.</li>
                        <li><strong>Reader Contributions / Tips</strong> – Readers may voluntarily support Authors with small payments or "virtual gifts."</li>
                        <li><strong>Competitions & Rewards</strong> – Authors may earn cash prizes by participating in contests organized by the Firm.</li>
                        <li><strong>Licensing & Adaptations</strong> – Selected stories may be adapted into audio, video, or OTT productions, with Authors receiving royalties.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.2 Virtual Coins / Credits</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Some Monetization Plans may operate through virtual coins or credits that Readers purchase via supported payment channels.</li>
                        <li>Coins/credits have no real-world currency value and can only be used within the Application.</li>
                        <li>Once purchased, coins/credits are non-refundable unless otherwise required by law.</li>
                        <li>Promotional or bonus coins/credits may be issued by the Firm and will be applied first during redemption.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.3 Earnings for Authors</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Eligible Authors will earn a share of revenue from Monetization Plans based on usage parameters such as reads, unlocks, or contributions.</li>
                        <li>The revenue share percentage will be communicated separately in the Author Earnings Policy and may be revised from time to time.</li>
                        <li>Earnings will be credited to the Author's account and may be withdrawn to their linked bank account once they reach the minimum payout threshold (e.g., ₹100).</li>
                        <li>Payments will be subject to applicable taxes, deductions, and banking charges. Authors are solely responsible for compliance with tax laws.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.4 Independent Contractor Status</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Authors are considered independent contractors, not employees or agents of Funtel Stories.</li>
                        <li>Earnings are in the nature of revenue share, not salary, wages, or employment benefits.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.5 Non-Transferability</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Coins/credits purchased by Readers and Earnings received by Authors are non-transferable to other Users.</li>
                        <li>Any benefits unlocked using coins/credits or subscriptions cannot be reassigned or sold.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.6 Forfeiture of Earnings</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>If a User's account is terminated for violation of these Terms, all unused coins/credits and unpaid earnings may be forfeited.</li>
                        <li>Inactivity for a period of 12 months may also lead to forfeiture of unused coins/credits and pending earnings, unless the User updates their details as requested.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.7 Refund Policy</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Subscription or coin/credit payments are generally non-refundable.</li>
                    </ul>
                </div>

                <!-- Firm as Intermediary -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">8. Firm as Intermediary</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">8.1 User Control over Content</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Funtel Stories acts only as a platform provider.</li>
                        <li>All Published Works and Inputs are created, uploaded, and controlled solely by the respective Authors or Users.</li>
                        <li>The Firm does not edit, alter, or interfere with the Content except as required under these Terms or by law.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">8.2 Intermediary Role under Indian Law</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Funtel Stories is an "intermediary" as defined under the Information Technology Act, 2000 and related rules.</li>
                        <li>As an intermediary, the Firm is not liable for any Content posted by Users, provided it complies with legal requirements.</li>
                        <li>Responsibility for the Content rests entirely with the User who uploads it.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">8.3 Legal Compliance</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>If any Published Work or Input is found to be in violation of applicable laws (e.g., IT Act, Copyright Act, Penal Code), the Firm is obligated to take down such Content when notified by:
                            <ul class="list-circle pl-6 mt-2 space-y-1">
                                <li>A competent authority,</li>
                                <li>A court of law, or</li>
                                <li>Any lawful investigation agency.</li>
                            </ul>
                        </li>
                        <li>Users must comply with and accept such actions without dispute.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">8.4 Duty to Act on Complaints</h3>
                    <p class="mb-2">Upon receiving a valid complaint regarding objectionable or unlawful Content, the Firm will:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Review the complaint,</li>
                        <li>Take down or restrict access to the Content (if justified), and</li>
                        <li>Inform the concerned User of the action taken.</li>
                    </ul>
                </div>

                <!-- Liability & Indemnity -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">9. Liability & Indemnity</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">9.1 No Warranty of Service</h3>
                    <p class="mb-2">All Services and Content on Funtel Stories are provided on an "as is" and "as available" basis.</p>
                    <p class="mb-2">The Firm does not warrant that:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>The Website/Application will function without errors, interruptions, or delays.</li>
                        <li>The Services will be free from viruses, malware, or harmful components.</li>
                        <li>All Content available on the platform is accurate, reliable, or up to date.</li>
                    </ul>
                    <p>Users access the Services at their own risk.</p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">9.2 Disclaimer of Endorsement</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Funtel Stories does not endorse, support, or verify any Published Works uploaded by Authors.</li>
                        <li>The views expressed in Published Works are solely those of the respective Authors.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">9.3 User Responsibility for Breach</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You are solely responsible for any violation of these Terms or applicable laws.</li>
                        <li>You will also be liable for any harm caused to the Firm, its partners, or other Users due to your actions.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">9.4 Indemnity</h3>
                    <p class="mb-2">You agree to indemnify and hold harmless Funtel Stories, its partners, employees, and associates from any claims, damages, liabilities, or expenses (including legal costs) arising from:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Your use of the Services,</li>
                        <li>Any Content you upload or publish,</li>
                        <li>Your breach of these Terms,</li>
                        <li>Your violation of third-party rights.</li>
                    </ul>
                    <p>The Firm reserves the right to defend itself in such cases and recover costs from you.</p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">9.5 Limitation of Liability</h3>
                    <p class="mb-2">To the maximum extent permitted by law, Funtel Stories shall not be liable for:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Indirect, incidental, consequential, or punitive damages,</li>
                        <li>Loss of profits, goodwill, data, or reputation,</li>
                        <li>Any technical failures, downtime, or interruptions in Services.</li>
                    </ul>
                    <p>The total aggregate liability of Funtel Stories, for any claim arising out of or related to your use of the Services, shall not exceed ₹10,000 (Indian Rupees Ten Thousand only).</p>
                </div>

                <!-- Grievance Redressal -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50" id="contact">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">10. Grievance Redressal</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">10.1 Appointment of Grievance Officer</h3>
                    <p class="mb-4">
                        In accordance with the Information Technology Act, 2000 and the Information Technology (Intermediaries Guidelines and Digital Media Ethics Code) Rules, 2021, Funtel Stories has designated a Grievance Officer to address user concerns.
                    </p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">10.2 How to File a Complaint</h3>
                    <p class="mb-2">If you believe that any Content on Funtel Stories:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Violates these Terms of Service,</li>
                        <li>Breaches your rights (including copyright, privacy, or publicity rights),</li>
                        <li>Is unlawful, harmful, or objectionable,</li>
                    </ul>
                    <p class="mb-2">you may submit a complaint by contacting our Grievance Officer with the following details:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Your full name, contact details (address, phone number, valid email ID).</li>
                        <li>Description of the Content alleged to be in violation.</li>
                        <li>Nature of the complaint (e.g., copyright infringement, abusive content, privacy violation).</li>
                        <li>Exact URL or location of the Content on the Website/Application.</li>
                        <li>Supporting documents, if any, to substantiate the claim.</li>
                        <li>Signed declaration (physical or electronic signature) confirming that the information provided is true and accurate.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">10.3 Resolution Timeline</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>The Firm will acknowledge your complaint within 48 hours.</li>
                        <li>A decision on the complaint will be taken within 15 (fifteen) days of receipt.</li>
                        <li>If Content is found to be in violation, it may be removed or restricted, and further action may be taken against the User responsible.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">10.4 Contact Details</h3>
                    <div class="bg-wattpad-primary/10 p-4 rounded-lg">
                        <h3 class="font-bold mb-2">Grievance Officer</h3>
                        <p class="mb-1">📧 Email: contact&#64;funtelstories.com</p>
                        <p>📍 Address: 8th Floor, Balaji Infotech Park, Plot No. A-278, Wagle Industrial Estate, Road No. 16 A, Lane Next to Wagale Police Station, Thane (W) - 400 604</p>
                    </div>
                </div>

                <!-- Miscellaneous -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">11. Miscellaneous</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">11.1 Modification of Terms</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Funtel Stories reserves the right to amend, update, or modify these Terms of Service at any time.</li>
                        <li>Such modifications will take effect immediately upon being posted on the Website/Application.</li>
                        <li>Users are responsible for reviewing the Terms regularly to stay updated. Continued use of the Services after such changes will be considered as acceptance of the revised Terms.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">11.2 Governing Law & Jurisdiction</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>These Terms, Privacy Policy, and all related policies are governed by the laws of India.</li>
                        <li>Any disputes arising between Users and the Firm shall be subject to the exclusive jurisdiction of the courts at [Insert City, India].</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">11.3 Conflict of Language Versions</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>In case of any conflict between the English version of these Terms and any translated version (including Hindi), the English version shall prevail.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">11.4 Assignment</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>The rights and obligations under these Terms are personal to each User and cannot be assigned to any third party without written consent of the Firm.</li>
                        <li>However, Funtel Stories may assign its rights and obligations under these Terms to its partners, successors, or affiliates (for example, in the event of a merger, restructuring, or expansion).</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">11.5 Severability</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>If any provision of these Terms is found to be invalid or unenforceable under applicable law, the remaining provisions shall remain valid and enforceable.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">11.6 Entire Agreement</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>These Terms, along with the Privacy Policy, Community Guidelines, and any additional policies published by Funtel Stories, constitute the entire agreement between the User and the Firm.</li>
                    </ul>
                </div>
            </div>
        </section>

    
    </main>